import React, { useEffect, useContext } from 'react';
import { Navigate, useLocation, UNSAFE_NavigationContext } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Define public paths that don't require authentication
const PUBLIC_PATHS = [
  '/',
  '/login',
  '/careers',
  '/pricing',
  '/legal',
  '/legal/terms',
  '/legal/privacy',
  '/legal-terms-privacy'
];

/**
 * App Route Guard - Mimics Next.js middleware behavior for React applications
 * 
 * This component provides middleware-like functionality for protecting routes
 * based on authentication status, similar to Next.js App Router middleware
 */
const AppRouteGuard = ({ children }) => {
  const { user, loading } = useAuth();

  // Guard against being mounted outside a Router (prevents useLocation crash)
  const routerCtx = useContext(UNSAFE_NavigationContext);
  if (!routerCtx) {
    // No Router available: just render children (e.g., during tests or odd mounts)
    return <>{children}</>;
  }

  const location = useLocation();

  const isPublicPath = PUBLIC_PATHS?.some(
    (path) => location?.pathname === path || location?.pathname?.startsWith(`${path}/`)
  );

  // Optional: show a loading state only for protected routes
  if (loading && !isPublicPath) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-sm">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Example hard redirect: if route is protected and user is anon, send to login
  if (!loading && !isPublicPath && !user) {
    return <Navigate to="/login" replace />;
  }

  // Dev only logging + indicator
  const showRouteIndicator = process.env?.NODE_ENV === "development";
  useEffect(() => {
    if (process.env?.NODE_ENV === "development") {
      const routeType = isPublicPath ? "PUBLIC" : "PROTECTED";
      const authStatus = user ? "AUTHENTICATED" : "ANONYMOUS";
      // eslint-disable-next-line no-console
      console.log(`[Route Guard] ${routeType} ${location?.pathname} (${authStatus})`);
    }
  }, [location?.pathname, isPublicPath, user]);

  return (
    <>
      {showRouteIndicator && (
        <div className="fixed top-0 right-0 z-50 bg-black/75 text-white px-2 py-1 text-xs font-mono rounded-bl">
          {isPublicPath ? "🌐 PUBLIC" : "🔒 PROTECTED"} | {user ? "✅ AUTH" : "❌ ANON"}
        </div>
      )}
      {children}
    </>
  );
};

export default AppRouteGuard;